import type {SerializedError} from '@reduxjs/toolkit';
import type {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import type {Dispatch} from 'react';
import type {GuestSummary} from '~/ViewInvitation/types/api';
import type {Guest, RsvpResponse} from '~/ViewInvitation/types/guests';
import {SortBy, type SortDirection} from '~/ViewInvitation/types/reducer';

export enum ActionTypes {
  SORT_GUESTS = 'SORT_GUESTS',
  SORT_DIRECTION = 'SORT_DIRECTION',
  FILTER_GUESTS = 'FILTER_GUESTS',
  SET_GUESTS = 'SET_GUESTS',
  SEARCH_GUESTS = 'SEARCH_GUESTS',
  RESET_GUESTS = 'RESET_GUESTS',
  SET_ERROR = 'SET_ERROR',
  CLEAR_ERROR = 'CLEAR_ERROR',
}

export interface SortGuestsAction {
  type: ActionTypes.SORT_GUESTS;
  sortType: SortBy;
  sortDirection?: SortDirection;
}

export interface SortGuestsDirectionAction {
  type: ActionTypes.SORT_DIRECTION;
  sortDirection: SortDirection;
}

export interface FilterGuestsAction {
  type: ActionTypes.FILTER_GUESTS;
  rsvpResponse?: RsvpResponse;
}

export interface SetGuestsAction {
  type: ActionTypes.SET_GUESTS;
  guests: Guest[];
  summary: GuestSummary;
}

export interface SearchGuestsAction {
  type: ActionTypes.SEARCH_GUESTS;
  query: string | undefined;
}

export interface ResetGuestsAction {
  type: ActionTypes.RESET_GUESTS;
}

export interface SetErrorAction {
  type: ActionTypes.SET_ERROR;
  error: FetchBaseQueryError | SerializedError;
}

export interface ClearErrorAction {
  type: ActionTypes.CLEAR_ERROR;
}

export type Actions =
  | SortGuestsAction
  | SortGuestsDirectionAction
  | FilterGuestsAction
  | SetGuestsAction
  | SearchGuestsAction
  | ResetGuestsAction
  | SetErrorAction
  | ClearErrorAction;

export const sortGuests = (
  dispatch: Dispatch<Actions>,
  sortType: SortBy,
  sortDirection?: SortDirection
) => {
  const action: SortGuestsAction = {
    type: ActionTypes.SORT_GUESTS,
    sortType,
  };
  if (sortDirection) {
    action.sortDirection = sortDirection;
  }
  dispatch(action);
};

export const setSortDirection = (dispatch: Dispatch<Actions>, sortDirection: SortDirection) =>
  dispatch({
    type: ActionTypes.SORT_DIRECTION,
    sortDirection,
  });

export const filterGuests = (dispatch: Dispatch<Actions>, rsvpResponse?: RsvpResponse) =>
  dispatch({
    type: ActionTypes.FILTER_GUESTS,
    rsvpResponse,
  });

export const setGuests = (dispatch: Dispatch<Actions>, guests: Guest[], summary: GuestSummary) =>
  dispatch({
    type: ActionTypes.SET_GUESTS,
    guests,
    summary,
  });

export const searchGuests = (dispatch: Dispatch<Actions>, query: string | undefined) =>
  dispatch({
    type: ActionTypes.SEARCH_GUESTS,
    query,
  });

export const resetGuests = (dispatch: Dispatch<Actions>) =>
  dispatch({
    type: ActionTypes.RESET_GUESTS,
  });

export const setError = (
  dispatch: Dispatch<Actions>,
  error: FetchBaseQueryError | SerializedError
) =>
  dispatch({
    type: ActionTypes.SET_ERROR,
    error,
  });
